import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import { countryService } from "../../utils/services/country.service";

const CountryModal = ({ open, handleClose, onAddData, selectedCountry }) => {
  const [countryDetails, setCountryDetails] = useState({
    name: "",
    code2: "",
    code3: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const initialData = () => {
    setCountryDetails({
      name: "",
      code2: "",
      code3: "",
    });
    setFormErrors({});
  };

  const isValidCode = (code, length) =>
    new RegExp(`^[A-Z]{${length}}$`).test(code.trim());

  const validateForm = () => {
    const errors = {};

    if (!countryDetails.name) {
      errors.name = "Country name is required.";
    }

    if (!countryDetails.code2) {
      errors.code2 = "2-letter code is required.";
    } else if (!isValidCode(countryDetails.code2, 2)) {
      errors.code2 = "Code2 must be exactly 2 uppercase letters.";
    }

    if (!countryDetails.code3) {
      errors.code3 = "3-letter code is required.";
    } else if (!isValidCode(countryDetails.code3, 3)) {
      errors.code3 = "Code3 must be exactly 3 uppercase letters.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (selectedCountry) {
      setCountryDetails({
        name: selectedCountry?.name || "",
        code2: selectedCountry?.code2 || "",
        code3: selectedCountry?.code3 || "",
      });
    } else {
      initialData();
    }
  }, [selectedCountry]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCountryDetails((prevData) => ({
      ...prevData,
      [name]: value.toUpperCase(),
    }));
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const { name, code2, code3 } = countryDetails;
      await countryService.edit({ name, code2, code3 }, selectedCountry._id);
      onAddData && onAddData();
      toast.success("Country updated successfully!");
      initialData();
      handleClose();
    } catch (e) {
      toast.error("Failed to update country.");
      console.error(e);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await countryService.create(countryDetails);
      onAddData && onAddData();
      toast.success("Country created successfully!");
      initialData();
      handleClose();
    } catch (e) {
      toast.error("Failed to create country.");
      console.error(e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        initialData();
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          style={{ fontFamily: "Poppins", fontSize: 24, fontWeight: 500 }}
        >
          {selectedCountry ? "Edit Country" : "Create Country"}
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTypography>Country Name</CustomTypography>
            <TextField
              size="small"
              name="name"
              value={countryDetails.name}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTypography>Code2 </CustomTypography>
            <TextField
              size="small"
              name="code2"
              value={countryDetails.code2}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              inputProps={{ maxLength: 2 }}
              error={!!formErrors.code2}
              helperText={formErrors.code2}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTypography>Code3 </CustomTypography>
            <TextField
              size="small"
              name="code3"
              value={countryDetails.code3}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              inputProps={{ maxLength: 3 }}
              error={!!formErrors.code3}
              helperText={formErrors.code3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: 15 }}>
        <Button
          onClick={() => {
            handleClose();
            initialData();
          }}
          variant="outlined"
          style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => (selectedCountry ? handleEditData(e) : handleSave(e))}
          variant="contained"
          style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}
        >
          {selectedCountry ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountryModal;
